import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings, Check } from "lucide-react";
import { useLanguage } from "@/lib/useLanguage";

const STORAGE_KEY = "orthocare_cookie_consent";

const I18N = {
  pt: {
    title: "Respeitamos a sua privacidade",
    desc: "Utilizamos cookies essenciais para o funcionamento do site e cookies analíticos para melhorar o serviço. Pode aceitar todos ou personalizar as suas preferências.",
    essential: "Essenciais",
    essentialDesc: "Necessários para o funcionamento do site (sessão, segurança). Sempre ativos.",
    analytics: "Analíticos",
    analyticsDesc: "Ajudam-nos a compreender como utiliza o site, de forma anónima.",
    acceptAll: "Aceitar todos",
    acceptSelection: "Guardar seleção",
    reject: "Apenas essenciais",
    customize: "Personalizar",
    privacy: "Política de Privacidade",
    details: "Ver política de cookies"
  },
  en: {
    title: "We respect your privacy",
    desc: "We use essential cookies for the site to work and analytics cookies to improve the service. You can accept all or customise your preferences.",
    essential: "Essential",
    essentialDesc: "Required for the site to work (session, security). Always active.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how you use the site, anonymously.",
    acceptAll: "Accept all",
    acceptSelection: "Save selection",
    reject: "Essential only",
    customize: "Customise",
    privacy: "Privacy Policy",
    details: "See cookie policy"
  }
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {}
}

export default function CookieConsent() {
  const { lang } = useLanguage();
  const t = I18N[lang] || I18N.pt;
  const [consent, setConsent] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const c = loadConsent();
    setConsent(c);
    if (!c) {
      const id = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  const notifyConsent = (v) => {
    window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: v }));
  };

  const handleAcceptAll = () => {
    const v = { essential: true, analytics: true };
    saveConsent(v);
    setConsent(v);
    setOpen(false);
    notifyConsent(v);
  };

  const handleReject = () => {
    const v = { essential: true, analytics: false };
    saveConsent(v);
    setConsent(v);
    setOpen(false);
    notifyConsent(v);
  };

  const handleSaveSelection = () => {
    const v = { essential: true, analytics };
    saveConsent(v);
    setConsent(v);
    setOpen(false);
    notifyConsent(v);
  };

  if (!open || consent) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <div className="shrink-0 w-10 h-10 rounded-full bg-[#076f77]/10 flex items-center justify-center">
            <Cookie className="w-5 h-5 text-[#076f77]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900 mb-1">{t.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t.desc}{" "}
              <Link to="/privacy" className="text-[#076f77] font-medium hover:underline">
                {t.privacy}
              </Link>
              {" · "}
              <Link to="/cookies" className="text-[#076f77] font-medium hover:underline">
                {t.details}
              </Link>
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="shrink-0 text-slate-500 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        {expanded && (
          <div className="px-4 sm:px-5 pb-2 space-y-3">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center h-5">
                <input type="checkbox" checked disabled className="w-4 h-4 accent-[#076f77]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">{t.essential}</p>
                <p className="text-xs text-slate-500">{t.essentialDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="w-4 h-4 accent-[#076f77]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">{t.analytics}</p>
                <p className="text-xs text-slate-500">{t.analyticsDesc}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 p-4 sm:px-5 sm:py-4 bg-slate-50 border-t border-slate-100">
          {!expanded ? (
            <>
              <button
                onClick={handleReject}
                className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors min-h-[40px]">
                {t.reject}
              </button>
              <button
                onClick={() => setExpanded(true)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-1.5 min-h-[40px]">
                <Settings className="w-4 h-4" /> {t.customize}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-[#076f77] rounded-lg hover:bg-[#0a8a93] transition-colors inline-flex items-center justify-center gap-1.5 min-h-[40px] sm:ml-auto">
                <Check className="w-4 h-4" /> {t.acceptAll}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReject}
                className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors min-h-[40px]">
                {t.reject}
              </button>
              <button
                onClick={handleSaveSelection}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-[#076f77] rounded-lg hover:bg-[#0a8a93] transition-colors inline-flex items-center justify-center gap-1.5 min-h-[40px] sm:ml-auto">
                <Check className="w-4 h-4" /> {t.acceptSelection}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}